import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { onUpdateSubStep } from "../../reducers/onboardingDataReducer";
import { onUpdateSubStep as onUpdateSubStepSecurities } from "../../reducers/securitiesReducer";
import { BasicStep } from "../../types";
import { Stepper } from "@allaria-tech/matter/Stepper";
import type { StepperStep } from "@allaria-tech/matter/Stepper";
import { isBackNavigationDisabled } from "../TemplateInterpreter/utils";
import styles from "./SubStepper.module.scss";

interface Props {
  currentSubStep: number | null;
  subSteps: BasicStep[] | undefined;
}

const SubStepper = ({ currentSubStep, subSteps }: Props) => {
  const dispatch = useAppDispatch();

  const { onboardingData, securities, activeTemplate } = useAppSelector(
    (state) => state,
  );

  const { lastCompletedSubStep, completedSteps, step } =
    activeTemplate.onboardingType === "SECURITIES"
      ? securities.stepData
      : onboardingData.stepData;

  const disabledSubStep =
    activeTemplate.onboardingType === "SECURITIES"
      ? null
      : onboardingData.stepData.disabledSubStep;

  const formValues =
    (activeTemplate.onboardingType === "SECURITIES"
      ? securities.formValues
      : onboardingData.formValues) ?? {};

  if (!currentSubStep || !subSteps?.length) return null;

  const blocksBackNavigation = (subStep: BasicStep) =>
    !!subStep.disableButtonBack ||
    isBackNavigationDisabled(
      subStep.disableBackNavigation,
      formValues,
      Number(subStep.step) + 1 < currentSubStep ? "completed" : "disabled",
    );

  const lastLockedSubStep = subSteps.reduce((locked, subStep) => {
    const subStepNumber = Number(subStep.step);
    return subStepNumber < currentSubStep && blocksBackNavigation(subStep)
      ? Math.max(locked, subStepNumber)
      : locked;
  }, 0);

  const isSubStepLocked = (subStep: number) => subStep <= lastLockedSubStep;

  const isSubStepDisabled = (subStep: number) =>
    (!!disabledSubStep && disabledSubStep === subStep) ||
    isSubStepLocked(subStep) ||
    (!completedSteps?.includes(Number(step)) &&
      subStep > lastCompletedSubStep + 1);

  const steps: StepperStep[] = subSteps.map((subStep) => ({
    label: subStep.titleOfStep || subStep.name || "",
    completed: Number(subStep.step) < currentSubStep,
    locked: isSubStepLocked(Number(subStep.step)),
  }));

  const currentIndex = subSteps.findIndex(
    (subStep) => Number(subStep.step) === currentSubStep,
  );

  const onStepClick = (index: number) => {
    const target = subSteps[index];
    if (!target) return;
    const subStep = Number(target.step);
    if (isSubStepDisabled(subStep)) return;
    dispatch(
      activeTemplate.onboardingType === "SECURITIES"
        ? onUpdateSubStepSecurities(subStep)
        : onUpdateSubStep(subStep),
    );
  };

  return (
    <Stepper
      variant="minimalist"
      steps={steps}
      currentStep={currentIndex < 0 ? 0 : currentIndex}
      onStepClick={onStepClick}
      className={styles.stepper}
    />
  );
};

export default SubStepper;
